import { PayloadAction } from '@reduxjs/toolkit';
import {
  PartialSheetViews,
  PartialSheets,
  Sheet,
  SheetGroups,
  SheetHistory,
  SheetManagerHistory,
  SheetViews,
  Sheets,
  EntityIdToName,
  TabSyncedChanges,
  HistoryActionType,
} from '../types';
import { SheetManagerSliceState as State } from './types';
import { UserStorageDataType } from '../../core/database/schemas';
import { sheetConstants } from '../../windows/constants/sheetConstants';
import { SheetManagementSet } from '../types/sheet/draft';
import { current } from 'immer';

// #region types
export interface UpdateSheetPayload {
  past: SheetHistory;
  present: SheetHistory;

  //   updatedSheet?: Sheet;
  updatedSheets?: PartialSheets;
  updatedGroups?: SheetGroups;
  updatedSheetViews?: PartialSheetViews;
  updatedSheetsIdToName?: EntityIdToName;
}

export interface UpdateSheetWithHistoryPayload
  extends Pick<
    UpdateSheetPayload,
    | 'updatedSheets'
    | 'updatedGroups'
    | 'updatedSheetsIdToName'
    | 'updatedSheetViews'
    | 'present'
    | 'past' // past: To Be Saved in db as either newFuture | or newPast
  > {
  history: SheetManagerHistory;
  historyAction: UserStorageDataType.Undo | UserStorageDataType.Redo;
  sheetsToRemoveFromMem: string[];
}

export interface ManualModeChangesDiscarded {
  history: SheetManagerHistory;
  updatedGroups: SheetGroups;
  updatedSheetsIdToName: EntityIdToName;
  disable?: boolean;
  updatedSheets: Sheets;
  updatedSheetViews: PartialSheetViews;
  sheetsToRemoveFromMem: string[];
}

interface ManualModeChangesSaved {
  sheets: Sheets;
  disable?: boolean;
}

interface AddSharedChanges {
  sheetsToRemoveFromMem?: string[];
  updatedGroups?: SheetGroups;
  updatedSheetsIdToName?: EntityIdToName;
  updatedSheet?: Sheet;
}

// #endregion

// #region Helpers
const updateSheetsState = (
  state: State,
  past: SheetHistory,
  present: SheetHistory,
  historyAction:
    | UserStorageDataType.Undo
    | UserStorageDataType.Redo
    | UserStorageDataType.History,
  updatedSheets?: PartialSheets,
  updatedGroups?: SheetGroups,
  updatedSheetsIdToName?: EntityIdToName,
  updatedSheetViews?: PartialSheetViews
) => {
  if (updatedSheets) {
    for (let id in updatedSheets) {
      if (state.sheets[id])
        state.sheets[id] = {
          ...state.sheets[id],
          ...updatedSheets[id],
        };

      if (state.isManualSave && !state.staleSheets[id])
        state.staleSheets[id] = false;

      // SheetViews
      if (updatedSheetViews && updatedSheetViews[id]) {
        state.sheetViews[id] = {
          ...state.sheetViews[id],
          ...updatedSheetViews[id],
        };
        if (present.sheetViews && present.sheetViews[id]) {
          state.history.presentSheetViews = {
            ...state.history.presentSheetViews,
            [id]: {
              ...present.sheetViews[id],
            },
          };
        }
      }
    }
  }
  if (updatedGroups) state.groups = updatedGroups;
  if (updatedSheetsIdToName) state.sheetsIdToName = updatedSheetsIdToName;

  if (state.isManualSave) {
    state.unSavedHistory.push({
      past,
      historyAction: historyAction,
    });
    if (present.sheetsIdToName)
      state.unSavedChanges.sheetsIdToName.push(present.sheetsIdToName);

    if (present.groups) state.unSavedChanges.groups.push(present.groups);
    if (present.sheetRoot)
      state.unSavedChanges.sheetRoot.push(present.sheetRoot);
    for (let sheetId in present.sheets) {
      const changes = present.sheets[sheetId];
      if (!state.unSavedChanges.sheets[sheetId])
        state.unSavedChanges.sheets[sheetId] = [];
      state.unSavedChanges.sheets[sheetId].push(changes);
    }
  }
};

// #endregion

// #region updateTabSharedChanges
const updateTabSharedChanges = (
  state: State,
  action: PayloadAction<
    Pick<
      UpdateSheetPayload,
      | 'past'
      | 'present'
      | 'updatedSheets'
      | 'updatedGroups'
      | 'updatedSheetsIdToName'
      | 'updatedSheetViews'
    > & {
      historyAction: TabSyncedChanges['historyAction'];
    }
  >
) => {
  const {
    past,
    present,
    updatedSheets,
    updatedGroups,
    historyAction,
    updatedSheetViews,
    updatedSheetsIdToName,
  } = action.payload;

  if (updatedSheets) {
    for (let id in updatedSheets) {
      if (state.sheets[id])
        state.sheets[id] = {
          ...state.sheets[id],
          ...updatedSheets[id],
        };

      if (state.isManualSave && !state.staleSheets[id])
        state.staleSheets[id] = false;

      // SheetViews
      if (updatedSheetViews && updatedSheetViews[id]) {
        state.sheetViews[id] = {
          ...state.sheetViews[id],
          ...updatedSheetViews[id],
        };
      }
    }
  }
  if (updatedGroups) state.groups = updatedGroups;
  if (updatedSheetsIdToName) state.sheetsIdToName = updatedSheetsIdToName;

  if (state.isManualSave) {
    state.unSavedHistory.push({
      past,
      historyAction: historyAction,
    });

    if (present.sheetsIdToName)
      state.unSavedChanges.sheetsIdToName.push(present.sheetsIdToName);

    if (present.groups) state.unSavedChanges.groups.push(present.groups);
    if (present.sheetRoot)
      state.unSavedChanges.sheetRoot.push(present.sheetRoot);
    for (let sheetId in present.sheets) {
      const changes = present.sheets[sheetId];
      if (!state.unSavedChanges.sheets[sheetId])
        state.unSavedChanges.sheets[sheetId] = [];
      state.unSavedChanges.sheets[sheetId].push(changes);
    }
  }
};
// #endregion

// #region actions
const updateSheetManagerState = (
  state: State,
  action: PayloadAction<UpdateSheetPayload>
) => {
  const {
    past,
    present,
    updatedSheets,
    updatedGroups,
    updatedSheetsIdToName,
    updatedSheetViews,
  } = action.payload;

  state.history.past = [...state.history.past, past].slice(
    -sheetConstants.maxUndo
  );
  state.history.future = [];

  updateSheetsState(
    state,
    past,
    present,
    UserStorageDataType.History,
    updatedSheets,
    updatedGroups,
    updatedSheetsIdToName,
    updatedSheetViews
  );
};

const updateSheetsStateWithPreCalculatedHistory = (
  state: State,
  action: PayloadAction<UpdateSheetWithHistoryPayload>
) => {
  const {
    history,
    updatedSheets,
    updatedGroups,
    updatedSheetsIdToName,
    past,
    present,
    sheetsToRemoveFromMem,
    historyAction,
    updatedSheetViews,
  } = action.payload;

  state.history = history;

  updateSheetsState(
    state,
    past,
    present,
    historyAction,
    updatedSheets,
    updatedGroups,
    updatedSheetsIdToName,
    updatedSheetViews
  );

  sheetsToRemoveFromMem.forEach((sheetId) => {
    delete state.sheets[sheetId];
  });
};

const manualModeChangesDiscarded = (
  state: State,
  action: PayloadAction<ManualModeChangesDiscarded>
) => {
  const {
    updatedSheets,
    disable,
    sheetsToRemoveFromMem,
    updatedGroups,
    updatedSheetsIdToName,
    history,
    updatedSheetViews,
  } = action.payload;
  if (disable) state.isManualSave = false;

  state.staleSheets = {};
  state.unSavedChanges = {
    sheetRoot: [],
    groups: [],
    sheets: {},
    sheetsIdToName: [],
  };
  state.unSavedHistory = [];
  // state.groups = groups;
  state.history = history;

  state.groups = updatedGroups;
  state.sheetsIdToName = updatedSheetsIdToName;

  sheetsToRemoveFromMem.forEach((sheetId) => {
    delete state.sheets[sheetId];
    delete state.sheetViews[sheetId];
  });
  for (let sheetId in updatedSheets) {
    if (state.sheets[sheetId]) state.sheets[sheetId] = updatedSheets[sheetId];
  }
  for (let sheetId in updatedSheetViews) {
    if (state.sheetViews[sheetId])
      state.sheetViews[sheetId] = {
        ...state.sheetViews[sheetId],
        ...updatedSheetViews[sheetId],
      };
  }
};

const manualModeChangesSaved = (
  state: State,
  action: PayloadAction<ManualModeChangesSaved>
) => {
  const { sheets, disable } = action.payload;
  for (let sheetId in sheets) {
    state.sheets[sheetId] = sheets[sheetId];
  }
  state.staleSheets = {};
  state.unSavedChanges = {
    sheetRoot: [],
    groups: [],
    sheets: {},
    sheetsIdToName: [],
  };
  state.unSavedHistory = [];
  if (disable) state.isManualSave = false;
};
// #endregion

// #region add shared changes
const addSharedChanges = (
  state: State,
  action: PayloadAction<AddSharedChanges>
) => {
  const {
    sheetsToRemoveFromMem,
    updatedGroups,
    updatedSheetsIdToName,
    updatedSheet,
  } = action.payload;
  if (sheetsToRemoveFromMem) {
    sheetsToRemoveFromMem.forEach((sheetId) => delete state.sheets[sheetId]);
  }
  if (updatedGroups) state.groups = updatedGroups;
  if (updatedSheetsIdToName) state.sheetsIdToName = updatedSheetsIdToName;
  if (updatedSheet) {
    const sheetId = updatedSheet.id;
    const sheetExistsInStale =
      state.isManualSave && state.staleSheets.hasOwnProperty(sheetId);
    if (sheetExistsInStale) state.staleSheets[sheetId] = true;
    state.sheets[sheetId] = updatedSheet;
  }
};
// #endregion

// #region close group
const closeGroup = (state: State, action: PayloadAction<string>) => {
  const groupId = action.payload;
  const group = state.groups[groupId];
  if (group) {
    group.sheets.forEach((sheetId) => {
      if (state.sheets[sheetId]) delete state.sheets[sheetId];
      if (state.sheetViews[sheetId]) delete state.sheetViews[sheetId];
    });
  }
};
// #endregion

const updateStateFromSheetsManagementSet = (
  state: State,
  action: PayloadAction<
    Pick<
      SheetManagementSet,
      | 'id'
      | 'deletedGroups'
      | 'deletedSheets'
      | 'updatedGroups'
      | 'updatedSheets'
      | 'updatedEntityIdToNameMap'
      | 'updatedSheetViews'
      | 'present'
      | 'past'
    > & {
      historyAction?: HistoryActionType;
    }
  >
) => {
  const set = action.payload;

  set.updatedGroups.forEach((value, key) => {
    state.sheetGroups[key] = value;
  });

  set.updatedSheets.forEach((value, key) => {
    state.sheets[key] = {
      ...state.sheets[key],
      ...value,
    };
  });

  set.deletedGroups.forEach((id) => delete state.sheetGroups[id]);
  set.deletedSheets.forEach((id) => {
    delete state.sheets[id];
    delete state.sheetViews[id];
  });

  set.updatedSheetViews.forEach((value, key) => {
    state.sheetViews[key] = {
      ...state.sheetViews[key],
      ...value,
    };
  });

  if (set.updatedEntityIdToNameMap)
    state.entityIdToNameMap = set.updatedEntityIdToNameMap;

  if (set.historyAction === HistoryActionType.Undo) {
    state.history2.past.pop();
    state.history2.future.unshift(set.past);
  } else if (set.historyAction === HistoryActionType.Redo) {
    state.history2.future.shift();
    state.history2.past.push(set.past);
  } else {
    state.history2.past = [...state.history2.past, set.past].slice(
      -sheetConstants.maxUndo
    );
    state.history2.future = [];
  }

  if (state.isManualSave) {
    state.changesToBeSaved.push({
      id: set.id,
      ...set.present,
    });
  }
};

export default {
  updateTabSharedChanges,
  manualModeChangesSaved,
  updateSheetManagerState,
  manualModeChangesDiscarded,
  updateSheetsStateWithPreCalculatedHistory,
  addSharedChanges,
  closeGroup,

  updateStateFromSheetsManagementSet,
};
